import { isWeekend } from 'date-fns';
import { useMemo } from 'react';

import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsProvider';
import { TTeamWiseEmployeeData } from '../../../types/analyticsTypes';
import { optionAll } from '../constants';
import { CheckInStatsData, TeamMemberStats } from '../types/checkInStats';

// Formatter strategy
const formatPercentage = (value: number): number => {
  return Number(value.toFixed(2));
};

const calculatePercentage = (count: number, total: number): number => {
  if (count >= total) return 100;
  if (total === 0 || count === 0) return 0;
  return formatPercentage((count / total) * 100);
};

interface UseTeamStatsProps {
  data: {
    currentPeriod: TTeamWiseEmployeeData;
    previousPeriod: TTeamWiseEmployeeData;
    todayOooMap: { [key: string]: boolean };
  };
  selectedTeam: { value: string; label: string } | null;
}

interface UseTeamStatsReturn {
  teamStats: CheckInStatsData | null;
  teamMembers: TeamMemberStats[];
  isLoading: boolean;
  error: string | null;
}

export function useTeamStats({
  data,
  selectedTeam,
}: UseTeamStatsProps): UseTeamStatsReturn {
  const { selectedPeriod } = useAnalytics();
  return useMemo(() => {
    if (!selectedTeam) {
      return {
        teamStats: null,
        teamMembers: [],
        isLoading: false,
        error: null,
      };
    }
    try {
      const isCurrentPeriod = selectedPeriod.value.substring(0, 4) === 'this';
      const now = new Date().toISOString().split('T')[1].substring(0, 5);
      let sequence = 0;
      if (now >= '12:30') sequence = 2;
      else if (now >= '08:30') sequence = 1;
      let currentPeriodData = data.currentPeriod[selectedTeam.label] || {};
      let previousPeriodData = data.previousPeriod[selectedTeam.label] || {};
      if (selectedTeam.value === optionAll.value) {
        currentPeriodData = Object.values(data.currentPeriod).reduce(
          (acc, team) => ({
            ...acc,
            ...team,
          }),
          {},
        );
        previousPeriodData = Object.values(data.previousPeriod).reduce(
          (acc, team) => ({
            ...acc,
            ...team,
          }),
          {},
        );
      }
      const employeeStats = Object.entries(currentPeriodData).map(
        ([userId, current]) => {
          let midDaySessions = current.totalWorkingDays;
          let endDaySessions = current.totalWorkingDays;
          if (isCurrentPeriod) {
            if (!current.isOOOToday && sequence === 0 && !isWeekend(new Date()))
              midDaySessions -= 1;
            if (!current.isOOOToday && sequence < 2 && !isWeekend(new Date()))
              endDaySessions -= 1;
          }
          const totalSessions = midDaySessions + endDaySessions;
          const adjustedCheckIns = {
            midDayCheckIns: Math.min(current.midDayCheckIns, midDaySessions),
            endDayCheckIns: Math.min(current.endDayCheckIns, endDaySessions),
          };
          const totalPercentage = calculatePercentage(
            adjustedCheckIns.midDayCheckIns + adjustedCheckIns.endDayCheckIns,
            totalSessions,
          );
          return {
            id: userId,
            name: current.name,
            didMissCheckins: totalSessions !== 0 && totalPercentage < 90,
            averageCheckIns: {
              percentage: totalPercentage,
            },
            midDayCheckIns: {
              count: adjustedCheckIns.midDayCheckIns,
              total: midDaySessions,
            },
            dayEndCheckIns: {
              count: adjustedCheckIns.endDayCheckIns,
              total: endDaySessions,
            },
          };
        },
      );
      const previousEmployeeStats = Object.entries(previousPeriodData).map(
        ([userId, current]) => {
          let midDaySessions = current.totalWorkingDays;
          let endDaySessions = current.totalWorkingDays;

          if (!current.isOOOToday && sequence === 0) midDaySessions -= 1;
          if (!current.isOOOToday && sequence < 2) endDaySessions -= 1;
          const totalSessions = midDaySessions + endDaySessions;
          const adjustedCheckIns = {
            midDayCheckIns: Math.min(current.midDayCheckIns, midDaySessions),
            endDayCheckIns: Math.min(current.endDayCheckIns, endDaySessions),
          };
          const totalPercentage = calculatePercentage(
            adjustedCheckIns.midDayCheckIns + adjustedCheckIns.endDayCheckIns,
            totalSessions,
          );
          return {
            id: userId,
            name: current.name,
            didMissCheckins: totalSessions !== 0 && totalPercentage < 90,
            averageCheckIns: {
              percentage: totalPercentage,
            },
            midDayCheckIns: {
              count: adjustedCheckIns.midDayCheckIns,
              total: midDaySessions,
            },
            dayEndCheckIns: {
              count: adjustedCheckIns.endDayCheckIns,
              total: endDaySessions,
            },
          };
        },
      );
      // Calculate team-wide averages
      const teamStats = {
        midDayCheckIns: 0,
        endDayCheckIns: 0,
        midDaySessions: 0,
        endDaySessions: 0,
      };
      const previousTeamPercentages = {
        midDayCheckIns: 0,
        endDayCheckIns: 0,
        midDaySessions: 0,
        endDaySessions: 0,
      };
      Object.values(employeeStats).forEach((employee) => {
        teamStats.midDayCheckIns += employee.midDayCheckIns.count;
        teamStats.endDayCheckIns += employee.dayEndCheckIns.count;
        teamStats.midDaySessions += employee.midDayCheckIns.total;
        teamStats.endDaySessions += employee.dayEndCheckIns.total;
      });

      // Calculate previous period percentages
      Object.values(previousEmployeeStats).forEach((employee) => {
        previousTeamPercentages.midDayCheckIns += employee.midDayCheckIns.count;
        previousTeamPercentages.endDayCheckIns += employee.dayEndCheckIns.count;
        previousTeamPercentages.midDaySessions += employee.midDayCheckIns.total;
        previousTeamPercentages.endDaySessions += employee.dayEndCheckIns.total;
      });

      // Calculate final averages with trends
      const averageCheckInsPercentage = calculatePercentage(
        teamStats.midDayCheckIns + teamStats.endDayCheckIns,
        teamStats.midDaySessions + teamStats.endDaySessions,
      );
      const prevAverageCheckInsPercentage = calculatePercentage(
        previousTeamPercentages.midDayCheckIns +
          previousTeamPercentages.endDayCheckIns,
        previousTeamPercentages.midDaySessions +
          previousTeamPercentages.endDaySessions,
      );
      const midDayCheckInsPercentage = calculatePercentage(
        teamStats.midDayCheckIns,
        teamStats.midDaySessions,
      );
      const dayEndCheckInsPercentage = calculatePercentage(
        teamStats.endDayCheckIns,
        teamStats.endDaySessions,
      );
      const finalTeamStats = {
        averageCheckIns: {
          percentage: averageCheckInsPercentage,
          trend: formatPercentage(
            averageCheckInsPercentage - prevAverageCheckInsPercentage,
          ),
        },
        midDayCheckIns: {
          percentage: midDayCheckInsPercentage,
        },
        dayEndCheckIns: {
          percentage: dayEndCheckInsPercentage,
        },
      };

      return {
        teamStats: finalTeamStats,
        teamMembers: employeeStats,
        isLoading: false,
        error: null,
      };
    } catch (err) {
      return {
        teamStats: null,
        teamMembers: [],
        isLoading: false,
        error: 'Error calculating team statistics',
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedTeam]);
}
